import { Autocomplete, TextField, CircularProgress, Chip, type AutocompleteProps } from "@mui/material";
import React, { type JSX } from "react";

import {
	useDebounce,
	type ErrorMap,
	type IdWithName,
	type BoundedValue,
} from "@repo/shared/common";

import { type AsyncSelectSearchHook } from "./BoundAsyncSelect";
import { areBoundFieldPropsEqual } from "./MemoHelper";

type PureAsyncMultiSelectProps<TItem> = {
	value: TItem[];
	onChangeValue: (v: TItem[]) => void;

	label: string;

	searchHook: AsyncSelectSearchHook<TItem>;

	getOptionLabel?: (item: TItem) => string;

	errors?: ErrorMap;
	errorKey?: string;

	additionalProps?: Omit<
		AutocompleteProps<TItem, true, false, false>,
		| "multiple"
		| "filterOptions"
		| "options"
		| "loading"
		| "value"
		| "onChange"
		| "onInputChange"
		| "getOptionLabel"
		| "isOptionEqualToValue"
		| "renderInput"
		| "renderTags"
	>;

	debounceMs?: number;
};

export function PureAsyncMultiSelect<TItem extends IdWithName>({
	value,
	onChangeValue,
	label,
	searchHook,
	errors,
	errorKey,
	additionalProps,
	debounceMs = 300,
	getOptionLabel = (x) => x.name,
}: PureAsyncMultiSelectProps<TItem>): JSX.Element {
	const { execute, items, loading, hasNextPage, endCursor, searchText, setSearchText } = searchHook;

	const debouncedSearch = useDebounce(searchText, debounceMs);

	/** Load first page whenever search changes */
	React.useEffect(() => {
		void execute(debouncedSearch, null);
	}, [debouncedSearch, execute]);

	/** Load next page ONLY when user scrolls to end */
	const handleScroll = (e: React.UIEvent<HTMLUListElement>): void => {
		const el = e.currentTarget;

		const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 20;

		if (atBottom && hasNextPage && !loading) {
			void execute(debouncedSearch, endCursor);
		}
	};

	const error = errors?.[errorKey ?? label];

	return (
		<Autocomplete
			multiple
			filterOptions={(x) => x} // disable MUI client filtering
			options={items}
			loading={loading}
			value={value}
			onChange={(_, v) => {
				onChangeValue(v);
			}}
			onInputChange={(_, v) => {
				setSearchText(v);
			}}
			getOptionKey={(x) => x.id}
			getOptionLabel={getOptionLabel}
			isOptionEqualToValue={(a: TItem, b: TItem) => a.id === b.id}
			// custom scroll behavior
			slotProps={{
				listbox: {
					onScroll: handleScroll,
					style: { maxHeight: 300, overflow: "auto" },
				},
			}}
			renderTags={(tagValue, getTagProps) =>
				tagValue.map((option, index) => {
					const { key, ...chipProps } = getTagProps({ index });
					return (
						<Chip
							key={key}
							label={getOptionLabel(option)}
							size="small"
							{...chipProps}
						/>
					);
				})
			}
			renderInput={(params) => (
				<TextField
					{...params}
					label={label}
					error={Boolean(error)}
					helperText={error}
					slotProps={{
						input: {
							...params.InputProps,
							endAdornment: (
								<>
									{loading ? <CircularProgress size={20} /> : null}
									{params.InputProps.endAdornment}
								</>
							),
						},
					}}
				/>
			)}
			{...additionalProps}
		/>
	);
}

// Memoized version: re-render only if relevant props changed
const MemoPureAsyncMultiSelect = React.memo(
	PureAsyncMultiSelect,
	areBoundFieldPropsEqual,
) as typeof PureAsyncMultiSelect;

export function BoundAsyncMultiSelect<TItem extends IdWithName>(
	props: {
		boundedValue: BoundedValue<TItem[]>;
	} & Omit<PureAsyncMultiSelectProps<TItem>, "value" | "onChangeValue" | "errorKey" | "errors">,
): JSX.Element {
	return (
		<MemoPureAsyncMultiSelect
			{...props}
			value={props.boundedValue.value}
			onChangeValue={(v) => {
				props.boundedValue.onChangeValue(v);
			}}
			errorKey={props.boundedValue.errorKey}
			errors={props.boundedValue.errorMap}
		/>
	);
}
